/* Collection page interactivity — React parity.
   Reference: philps-site/components/collections/CollectionPage.tsx

   Handles client-side:
     - Sidebar checkbox filter groups (OR within group, AND across groups)
     - Price max slider
     - Sort: popular | price-asc | price-desc | rating
     - Load-more pagination
     - Mobile filter drawer (open/close, scroll lock)
     - Drawer + desktop checkbox sync (when same section.id renders twice)
     - Empty state when no products match
*/
(function () {
  var sections = document.querySelectorAll('[data-collection-section]');
  sections.forEach(initSection);

  function initSection(root) {
    var grid = root.querySelector('[data-collection-grid]');
    if (!grid) return;

    var items = Array.prototype.slice.call(grid.querySelectorAll('[data-collection-item]'));
    if (items.length === 0) return;

    var pageSize = parseInt(root.getAttribute('data-page-size') || '9', 10);

    /* ---- Cache item meta as numbers / arrays once ----------------- */
    var meta = items.map(function (el) {
      var tagStr = (el.getAttribute('data-tags') || '').toLowerCase();
      var boxStr = (el.getAttribute('data-box-numbers') || '').toLowerCase();
      var servesStr = (el.getAttribute('data-serves') || '').toLowerCase();
      return {
        el: el,
        price: parseFloat(el.getAttribute('data-price')) || 0,
        rating: parseFloat(el.getAttribute('data-rating')) || 0,
        type: (el.getAttribute('data-type') || '').toLowerCase(),
        tags: tagStr ? tagStr.split(',').map(function (s) { return s.trim(); }).filter(Boolean) : [],
        boxNumbers: boxStr ? boxStr.split(',').map(function (s) { return s.trim(); }).filter(Boolean) : [],
        serves: servesStr ? servesStr.split(',').map(function (s) { return s.trim(); }).filter(Boolean) : [],
        sourceIndex: parseInt(el.getAttribute('data-source-index'), 10) || 0,
      };
    });

    /* ---- Conditional-trigger UI -----------------------------------
       Each entry maps a trigger option value (e.g. "pasties") to:
         - the heading text the main category filter should swap to,
         - the [data-collection-conditional] attribute value of the
           sub-filter block that reveals when the trigger is active,
         - the group key of that sub-filter (so we can clear its state
           when the trigger is deactivated).
       Order matters: when multiple triggers are active simultaneously
       the first wins for the heading swap. */
    var conditionalTriggers = [
      {
        optionValue: 'pasties',
        heading: 'Pasty type',
        conditionalAttr: 'pasties',
        subFilterGroupKey: 'box_size',
      },
      {
        optionValue: 'cream teas',
        heading: 'Includes',
        conditionalAttr: 'cream-teas',
        subFilterGroupKey: 'serves',
      },
    ];

    // Headings of any group that contains a trigger option. Stash the
    // original text so we can restore it on deactivation.
    var triggerHeadings = [];
    root.querySelectorAll('[data-collection-filter-group]').forEach(function (g) {
      if (g.getAttribute('data-collection-conditional')) return; // skip sub-filter blocks
      var hasTrigger = false;
      for (var i = 0; i < conditionalTriggers.length; i++) {
        if (g.querySelector('[data-collection-option][data-option-value="' + conditionalTriggers[i].optionValue + '"]')) {
          hasTrigger = true;
          break;
        }
      }
      if (hasTrigger) {
        var heading = g.querySelector('.collection-2026__filter-heading');
        if (heading) {
          heading.setAttribute('data-original-label', heading.textContent.trim());
          triggerHeadings.push(heading);
        }
      }
    });

    // Forced via metafield: filter_source = "pasties" or "cream teas".
    // When set, Liquid has already painted the matching trigger as
    // active (correct heading + sub-filter visible without [hidden]),
    // so JS should leave the toggle alone.
    var forcedMode = root.getAttribute('data-forced-mode'); // 'pasties' | 'cream-teas' | null

    /* ---- State ---------------------------------------------------- */
    var state = {
      priceCap: null,       // null = uncapped
      priceMax: null,       // configured slider max
      sort: 'popular',
      // map[groupKey] -> { source: 'type'|'tag', values: Set<string> }
      groups: {},
      visibleCount: pageSize,
    };

    /* ---- Wiring --------------------------------------------------- */
    // Sort menus (may be 2: mobile + desktop). Keep them in sync.
    var sortSelects = root.querySelectorAll('[data-collection-sort]');
    sortSelects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        state.sort = sel.value;
        sortSelects.forEach(function (s) { if (s !== sel) s.value = sel.value; });
        state.visibleCount = pageSize;
        apply();
      });
    });

    // Price slider (may be 2: sidebar + drawer)
    var priceSliders = root.querySelectorAll('[data-collection-price-slider]');
    if (priceSliders.length) {
      var sliderMax = parseFloat(priceSliders[0].getAttribute('max')) || 100;
      state.priceMax = sliderMax;
      priceSliders.forEach(function (slider) {
        slider.addEventListener('input', function () {
          state.priceCap = parseFloat(slider.value);
          // Sync sibling slider(s) + readout
          priceSliders.forEach(function (s) { if (s !== slider) s.value = slider.value; });
          var readouts = root.querySelectorAll('[data-collection-price-cap]');
          readouts.forEach(function (r) { r.textContent = String(state.priceCap); });
          state.visibleCount = pageSize;
          apply();
        });
      });
    }

    // Checkboxes — sync siblings sharing the same group/value (drawer ↔ desktop)
    var checkboxes = root.querySelectorAll('[data-collection-option]');
    checkboxes.forEach(function (cb) {
      cb.addEventListener('change', function () {
        var groupKey = cb.getAttribute('data-group-key');
        var value = (cb.getAttribute('data-option-value') || '').toLowerCase();
        // Determine source: option-level override wins (allows a
        // `type`-source group to host tag-matched options like Cream
        // Teas), otherwise fall back to the group's source.
        var groupEl = root.querySelector('[data-collection-filter-group][data-group-key="' + cssEscape(groupKey) + '"]');
        var groupSource = groupEl ? (groupEl.getAttribute('data-group-source') || 'tag') : 'tag';
        var perOptionSource = cb.getAttribute('data-option-source') || '';

        if (!state.groups[groupKey]) state.groups[groupKey] = { source: groupSource, values: new Set(), sources: {} };

        if (cb.checked) {
          state.groups[groupKey].values.add(value);
          if (perOptionSource) state.groups[groupKey].sources[value] = perOptionSource;
        } else {
          state.groups[groupKey].values.delete(value);
          delete state.groups[groupKey].sources[value];
        }
        // Sync all siblings with same group+value
        checkboxes.forEach(function (other) {
          if (other === cb) return;
          if (other.getAttribute('data-group-key') === groupKey && (other.getAttribute('data-option-value') || '').toLowerCase() === value) {
            other.checked = cb.checked;
          }
        });
        state.visibleCount = pageSize;
        togglePastiesUI();
        apply();
      });
    });

    // Clear all
    var clearButtons = root.querySelectorAll('[data-collection-clear-all]');
    clearButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { clearAll(); });
    });

    // Load more
    var loadMoreBtn = root.querySelector('[data-collection-load-more]');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', function () {
        state.visibleCount += pageSize;
        apply();
      });
    }

    // Drawer open / close
    var drawer = root.querySelector('[data-collection-drawer]');
    var openBtns = root.querySelectorAll('[data-collection-open-drawer]');
    var closeBtns = root.querySelectorAll('[data-collection-close-drawer]');

    openBtns.forEach(function (b) { b.addEventListener('click', openDrawer); });
    closeBtns.forEach(function (b) { b.addEventListener('click', closeDrawer); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer && !drawer.hasAttribute('hidden')) closeDrawer();
    });

    function openDrawer() {
      if (!drawer) return;
      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('collection-2026-drawer-open');
    }
    function closeDrawer() {
      if (!drawer) return;
      drawer.setAttribute('hidden', '');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('collection-2026-drawer-open');
    }

    /* ---- Helpers -------------------------------------------------- */
    function clearAll() {
      state.priceCap = state.priceMax;
      state.groups = {};
      state.visibleCount = pageSize;
      checkboxes.forEach(function (cb) { cb.checked = false; });
      priceSliders.forEach(function (s) { s.value = String(state.priceMax); });
      var readouts = root.querySelectorAll('[data-collection-price-cap]');
      readouts.forEach(function (r) { r.textContent = String(state.priceMax); });
      togglePastiesUI();
      apply();
    }

    function activeFilterCount() {
      var n = 0;
      Object.keys(state.groups).forEach(function (k) { n += state.groups[k].values.size; });
      if (state.priceCap !== null && state.priceMax !== null && state.priceCap < state.priceMax) n += 1;
      return n;
    }

    function matchesFilters(m) {
      // Price filter
      if (state.priceCap !== null && m.price > state.priceCap) return false;
      // Filter groups — AND across groups, OR within
      var keys = Object.keys(state.groups);
      for (var i = 0; i < keys.length; i++) {
        var g = state.groups[keys[i]];
        if (!g || g.values.size === 0) continue;
        var ok = false;
        g.values.forEach(function (v) {
          if (ok) return;
          // Per-option source override (e.g. a `type`-source group can
          // host an option that matches by tag). Falls back to the
          // group's declared source when no override is set.
          var s = (g.sources && g.sources[v]) || g.source;
          if (s === 'type') {
            if (m.type === v) ok = true;
          } else if (s === 'boxnum') {
            // Match if any of the product's variant numbers equals the
            // option value exactly (whole-number match — so filtering
            // by "1" doesn't pull in "10", "11", "12" etc.).
            for (var j = 0; j < m.boxNumbers.length; j++) {
              if (m.boxNumbers[j] === v) { ok = true; break; }
            }
          } else if (s === 'serves') {
            // Exact match against the product's custom.serves_quantity
            // values (already lowercased + split on commas).
            for (var k2 = 0; k2 < m.serves.length; k2++) {
              if (m.serves[k2] === v) { ok = true; break; }
            }
          } else {
            if (m.tags.indexOf(v) !== -1) ok = true;
          }
        });
        if (!ok) return false;
      }
      return true;
    }

    /* ---- Conditional category UI -----------------------------------
       Generic per-trigger logic: each trigger checks whether its
       option is selected, reveals/hides its sub-filter block, and
       clears the sub-filter state when going inactive. The heading
       swap is global — the first active trigger wins. */
    function triggerActive(value) {
      var active = false;
      Object.keys(state.groups).forEach(function (k) {
        if (active) return;
        var g = state.groups[k];
        if (g && g.values && g.values.has(value)) active = true;
      });
      return active;
    }

    function togglePastiesUI() {
      // Renamed in spirit but kept as the function name to minimise
      // diff with the existing call sites. Iterates all triggers.
      if (forcedMode) return; // Liquid painted everything; don't touch it.

      var firstActiveTrigger = null;
      conditionalTriggers.forEach(function (trigger) {
        var active = triggerActive(trigger.optionValue);

        // Reveal / hide the trigger's sub-filter block(s).
        root.querySelectorAll('[data-collection-conditional="' + trigger.conditionalAttr + '"]').forEach(function (b) {
          if (active) b.removeAttribute('hidden');
          else b.setAttribute('hidden', '');
        });

        // When deactivating, clear the sub-filter's selections from
        // state + DOM so they don't linger as ghost filters.
        if (!active && state.groups[trigger.subFilterGroupKey]) {
          state.groups[trigger.subFilterGroupKey].values.clear();
          state.groups[trigger.subFilterGroupKey].sources = {};
          root.querySelectorAll('[data-collection-option][data-group-key="' + trigger.subFilterGroupKey + '"]').forEach(function (cb) {
            cb.checked = false;
          });
        }

        if (active && !firstActiveTrigger) firstActiveTrigger = trigger;
      });

      // Global heading swap — first active trigger's heading wins.
      if (firstActiveTrigger) {
        root.setAttribute('data-pasties-active', 'true');
        triggerHeadings.forEach(function (h) {
          h.textContent = firstActiveTrigger.heading;
        });
      } else {
        root.removeAttribute('data-pasties-active');
        triggerHeadings.forEach(function (h) {
          h.textContent = h.getAttribute('data-original-label') || h.textContent;
        });
      }
    }

    function sortedList(list) {
      var copy = list.slice();
      switch (state.sort) {
        case 'price-asc':
          copy.sort(function (a, b) { return a.price - b.price; });
          break;
        case 'price-desc':
          copy.sort(function (a, b) { return b.price - a.price; });
          break;
        case 'rating':
          copy.sort(function (a, b) { return b.rating - a.rating; });
          break;
        default:
          copy.sort(function (a, b) { return a.sourceIndex - b.sourceIndex; });
      }
      return copy;
    }

    /* ---- Apply: filter -> sort -> reorder DOM -> show/hide ------- */
    function apply() {
      var filtered = meta.filter(matchesFilters);
      var sorted = sortedList(filtered);

      // Reorder DOM to reflect sort. Hide everything not in `sorted`.
      var matchedSet = new Set(sorted.map(function (m) { return m.el; }));
      // First hide everything that didn't match
      meta.forEach(function (m) {
        if (!matchedSet.has(m.el)) m.el.setAttribute('hidden', '');
      });
      // Then append matched items in order; only the first `visibleCount` are visible
      var fragment = document.createDocumentFragment();
      sorted.forEach(function (m, i) {
        if (i < state.visibleCount) {
          m.el.removeAttribute('hidden');
        } else {
          m.el.setAttribute('hidden', '');
        }
        fragment.appendChild(m.el);
      });
      grid.appendChild(fragment);

      // Update counts
      var visibleCount = Math.min(state.visibleCount, sorted.length);
      var totalCount = items.length;
      var filteredCount = sorted.length;

      var visEls = root.querySelectorAll('[data-collection-visible-count]');
      visEls.forEach(function (e) { e.textContent = String(filteredCount); });
      var visNouns = root.querySelectorAll('[data-collection-visible-noun]');
      visNouns.forEach(function (e) { e.textContent = filteredCount === 1 ? 'product' : 'products'; });
      var totEls = root.querySelectorAll('[data-collection-total-count]');
      totEls.forEach(function (e) { e.textContent = String(totalCount); });
      var shownEls = root.querySelectorAll('[data-collection-shown-count]');
      shownEls.forEach(function (e) { e.textContent = String(visibleCount); });
      var fEls = root.querySelectorAll('[data-collection-filtered-count]');
      fEls.forEach(function (e) { e.textContent = String(filteredCount); });

      // Active filter chip
      var count = activeFilterCount();
      var countEls = root.querySelectorAll('[data-collection-count]');
      countEls.forEach(function (e) { e.textContent = String(count); });
      // Show/hide the active-count chip on the mobile trigger
      var triggerChip = root.querySelector('.collection-2026__filter-trigger [data-collection-count]');
      if (triggerChip) {
        if (count > 0) triggerChip.removeAttribute('hidden'); else triggerChip.setAttribute('hidden', '');
      }
      // Show/hide the "Clear (n)" button(s)
      clearButtons.forEach(function (btn) {
        if (btn.classList.contains('collection-2026__clear')) {
          if (count > 0) btn.removeAttribute('hidden'); else btn.setAttribute('hidden', '');
        }
      });

      // Empty state
      var emptyEl = root.querySelector('[data-collection-empty]');
      if (emptyEl) {
        if (filteredCount === 0) emptyEl.removeAttribute('hidden');
        else emptyEl.setAttribute('hidden', '');
      }
      if (grid) {
        if (filteredCount === 0) grid.setAttribute('hidden', '');
        else grid.removeAttribute('hidden');
      }

      // Load-more visibility — hide the wrap (status + button) when
      // there are no filter results at all, and hide just the button
      // when everything that does match is already on screen. Keeps
      // the "Showing N of N" status visible as a confirmation while
      // suppressing a Load-more button that would do nothing.
      var moreWrap = root.querySelector('[data-collection-more-wrap]');
      var moreBtn = root.querySelector('[data-collection-load-more]');
      if (moreWrap) {
        if (filteredCount > 0) moreWrap.removeAttribute('hidden');
        else moreWrap.setAttribute('hidden', '');
      }
      if (moreBtn) {
        if (visibleCount < filteredCount) moreBtn.removeAttribute('hidden');
        else moreBtn.setAttribute('hidden', '');
      }
    }

    /* ---- Minimal CSS.escape polyfill for the group-key lookup --- */
    function cssEscape(s) {
      if (window.CSS && window.CSS.escape) return window.CSS.escape(s);
      return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) { return '\\' + c; });
    }

    /* ---- Initial paint ------------------------------------------- */
    apply();
  }
})();
